import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../_services/alert.service';
import { PostcodeAddress, PostcodesService } from '../../_services/postcodes.service';
import { PaginatorPagesDirective } from '../../../Shared/_directives/paginator-pages.directive';

// Postcodes: the address book built from "Find address" picks. Super admins
// can search, add, edit and delete entries. Laid out like Categories.
@Component({
    selector: 'app-postcodes',
    imports: [PaginatorPagesDirective, MainLayoutComponent, CommonModule, FormsModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, MatPaginatorModule],
    templateUrl: './postcodes.component.html',
    styleUrl: './postcodes.component.css'
})
export class PostcodesComponent implements OnInit, OnDestroy {
  rows: PostcodeAddress[] = [];
  total = 0;
  stats: { addresses: number; postcodes: number; counties: number } | null = null;
  pageIndex = 0;
  pageSize = 20;
  search = '';
  loading = true;
  loadError = '';

  // Add / edit slide-over
  editing: PostcodeAddress | null = null;
  saving = false;
  formError = '';
  // Delete confirmation
  deleting: PostcodeAddress | null = null;
  removing = false;

  private typed = new Subject<string>();
  private sub?: Subscription;

  constructor(private service: PostcodesService, private alertService: AlertService) {}

  ngOnInit(): void {
    this.sub = this.typed.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => { this.pageIndex = 0; this.load(); });
    this.load();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onSearch(value: string) {
    this.search = value;
    this.typed.next(value.trim());
  }

  onPage(e: PageEvent) {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.load();
  }

  load() {
    this.loading = true;
    this.loadError = '';
    this.service.list(this.search.trim(), this.pageIndex + 1, this.pageSize).subscribe({
      next: (r) => {
        this.rows = r.rows || [];
        this.total = r.total || 0;
        if (r.stats) this.stats = r.stats;
        this.loading = false;
      },
      error: (e) => {
        this.loading = false;
        this.rows = [];
        this.loadError = e?.error?.message || 'Could not load postcodes from the server.';
      },
    });
  }

  // ---- add / edit ----
  openAdd() {
    this.formError = '';
    const pc = this.looksLikePostcode(this.search) ? this.search.toUpperCase() : '';
    this.editing = { address1: '', address2: '', city: '', county: '', postalcode: pc };
  }

  openEdit(row: PostcodeAddress) {
    this.formError = '';
    this.editing = { ...row };
  }

  closeForm() {
    if (!this.saving) this.editing = null;
  }

  save() {
    const a = this.editing;
    if (!a || this.saving) return;
    if (!a.address1.trim() || !a.postalcode.trim()) {
      this.formError = 'Address line 1 and postcode are required.';
      return;
    }
    this.saving = true;
    this.formError = '';
    const req = a.id ? this.service.update(a.id, a) : this.service.create(a);
    req.subscribe({
      next: () => {
        this.saving = false;
        this.alertService.alert(a.id ? 'Address updated' : 'Address added', 'success');
        this.editing = null;
        this.load();
      },
      error: (e) => {
        this.saving = false;
        this.formError = e?.error?.message || 'Could not save the address';
      },
    });
  }

  // ---- delete ----
  askDelete(row: PostcodeAddress) {
    this.deleting = row;
  }

  confirmDelete() {
    const row = this.deleting;
    if (!row?.id || this.removing) return;
    this.removing = true;
    this.service.remove(row.id).subscribe({
      next: () => {
        this.removing = false;
        this.deleting = null;
        if (this.editing?.id === row.id) this.editing = null;
        this.alertService.alert('Address deleted', 'success');
        if (this.rows.length === 1 && this.pageIndex > 0) this.pageIndex--;
        this.load();
      },
      error: (e) => {
        this.removing = false;
        this.alertService.alert(e?.error?.message || 'Could not delete the address', 'error');
      },
    });
  }

  private looksLikePostcode(v: string): boolean {
    return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test((v || '').trim());
  }
}
